import React from 'react';
import { ImageProps } from '@components/image';
import generateId from '@helpers/generate-id';
import Wrapper from '@components/wrapper.styled';
import { RichTextBlock } from 'prismic-reactjs';
import { defaultTheme } from '@styles/theme.styled';
import Text from '@components/text';
import {
  ImageContainer,
  Container,
  MessageContainer,
  StyledSection,
  StyledImage,
  ItemWrapper,
  ItemIcon,
} from './text-and-image.styled';

interface ItemProps {
  description?: {
    richText: RichTextBlock[];
  };
  image?: ImageProps;
}

interface TextImageProps {
  title?: string;
  subheading?: RichTextBlock[];
  image?: ImageProps;
  align?: string;
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  backgroundImage?: ImageProps;
  condensedSection?: boolean;
  imageFit?: boolean;
  enclosureColor?: keyof typeof defaultTheme.backgroundColors;
  items?: ItemProps[];
}
const idGenerator = generateId();

const TextImage = ({
  title,
  subheading = [],
  image,
  align,
  backgroundColor = 'white',
  backgroundImage,
  imageFit,
  enclosureColor,
  items,
  condensedSection = false,
}: TextImageProps): JSX.Element => {
  const isSubheadingExisting: boolean = subheading?.length > 0 && subheading[0].text !== '';
  if (!title && !image && !isSubheadingExisting && !backgroundImage) {
    return <></>;
  }

  return (
    <StyledSection backgroundColor={backgroundColor} condensedSection={condensedSection}>
      <Wrapper>
        <Container enclosureColor={enclosureColor}>
          <MessageContainer align={align}>
            {title && (
              <Text
                type="title"
                tag="h2"
                stringText={title}
                fontColor={enclosureColor === 'dark' ? 'white' : 'secondary'}
                fontSize="heading3"
                margin="md"
              />
            )}
            {isSubheadingExisting && (
              <Text
                type="subheading"
                fontSize="subheading"
                fontColor="dark"
                richText={subheading}
              />
            )}
            {items &&
              items.map((item) => {
                const itemId = idGenerator.next().value;

                return (
                  <ItemWrapper>
                    {item.image && (
                      <ItemIcon
                        url={item.image.url}
                        alt={item.image.alt || ''}
                        gatsbyImageData={item.image.gatsbyImageData}
                      />
                    )}
                    {item.description?.richText && item.description.richText.length > 0 && (
                      <Text
                        key={itemId}
                        type="description"
                        richText={item.description.richText}
                        fontSize="body"
                        fontColor={enclosureColor === 'dark' ? 'white' : 'dark'}
                      />
                    )}
                  </ItemWrapper>
                );
              })}
          </MessageContainer>
          <ImageContainer>
            {(image?.gatsbyImageData || image?.url) && (
              <StyledImage
                url={image.url}
                alt={image.alt || ''}
                gatsbyImageData={image.gatsbyImageData}
                objectFit={imageFit ? 'contain' : 'cover'}
              />
            )}
          </ImageContainer>
        </Container>
      </Wrapper>
    </StyledSection>
  );
};

export default TextImage;
